import React, {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {
  Box,
  Text,
  Flex,
  Button,
  Icon,
  Badge,
  Spinner,
} from "@chakra-ui/react";
import {useNavigate} from "react-router-dom";
import {lightFormat} from "date-fns";
import {crud} from "../crudRequests";
import {fetchConsentStatus} from "./Consents/consentRequests";
import {useContext} from "react";
import {SocketContext} from "./Layout";
import ViewCompletedFrom from "./Froms/ViewCompletedFrom";
import CompleteForm from "./Froms/CompleteForm";
import {HiOutlineDocumentText, HiOutlineDocumentCheck} from "react-icons/hi2";
import {FiFileText, FiCheckCircle, FiClock, FiEye, FiEdit3} from "react-icons/fi";
import {MdExpandMore, MdChevronRight} from "react-icons/md";

function getApptProvider(appt) {
  return appt?.dName || appt?.doctorName || appt?.providerName || "";
}

function formatApptDate(appt) {
  const when = appt?.ISOdate || appt?.date || appt?.start || appt?.startDate;
  if (!when) return "";
  const d = new Date(typeof when === "number" ? when : when);
  return d.toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
}

function groupFormsByAppointment(forms, appointmentMap) {
  const groups = {};
  const ungrouped = [];
  forms.forEach((fm) => {
    if (fm.aid && appointmentMap[fm.aid]) {
      if (!groups[fm.aid]) groups[fm.aid] = [];
      groups[fm.aid].push(fm);
    } else {
      ungrouped.push(fm);
    }
  });
  const sortedGroups = Object.entries(groups).sort(([aidA], [aidB]) => {
    const a = appointmentMap[aidA];
    const b = appointmentMap[aidB];
    const aRaw = a?.ISOdate || a?.date || a?.start || a?.startDate || 0;
    const bRaw = b?.ISOdate || b?.date || b?.start || b?.startDate || 0;
    const aMs = typeof aRaw === "number" ? aRaw : new Date(aRaw).getTime() || 0;
    const bMs = typeof bRaw === "number" ? bRaw : new Date(bRaw).getTime() || 0;
    return bMs - aMs;
  });
  return {sortedGroups, ungrouped};
}

export function normalizeLocationIds(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === "string") return entry.trim();
        if (entry && typeof entry === "object" && typeof entry.lid === "string") {
          return entry.lid.trim();
        }
        return "";
      })
      .filter((entry) => entry && entry !== "all");
  }

  if (value && typeof value === "object" && typeof value.lid === "string") {
    const trimmed = value.lid.trim();
    return trimmed && trimmed !== "all" ? [trimmed] : [];
  }

  if (typeof value !== "string") {
    return [];
  }

  const trimmed = value.trim();
  return trimmed && trimmed !== "all" ? [trimmed] : [];
}

// A patient can belong to many locations, and a form assigned at any of them is
// still theirs to fill. Matching only the "selected" location (patient.lid[0])
// hid every form for patients whose first location happens to have none — the
// count on Home said 22 pending while this list rendered empty.
export function filterFormsByLocation(forms = [], patientLocationIds = []) {
  if (!patientLocationIds.length) {
    return forms;
  }

  return forms.filter((form) => {
    const formLids = normalizeLocationIds(form?.lid);
    // A form with no location is not scoped to one — never hide it.
    if (!formLids.length) return true;
    return formLids.some((lid) => patientLocationIds.includes(lid));
  });
}

export default function FormsStatus({state}) {
  const patient = state.patient;
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [assignedForms, setAssignedForms] = useState([]);
  const [completedForms, setCompletedForms] = useState([]);
  const [appointmentMap, setAppointmentMap] = useState({});
  const abortControllerRef = useRef(new AbortController());
  const socket = useContext(SocketContext);
  const [viewForm, setViewForm] = useState(null);
  const [completeForm, setCompleteForm] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  // Consents live in their own collection and their own signing surface — they
  // are not `form_submissions` and are not filled by the form engine. What is
  // shared is this INBOX: a patient should have one place that tells them
  // "something needs your signature", so a pending consent surfaces here and
  // links out. See PATIENT_CONSENT_DESIGN.md §4.
  const [consentStatus, setConsentStatus] = useState(null);
  const navigate = useNavigate();

  const updatedFormValues = useCallback(() => {
    setLoading(true);
    crud(
      state,
      [
        {
          db: state.db,
          collection: "form_submissions",
          parameters: [{pid: patient.pid, portalVisible: {$ne: false}}],
          method: "find",
        },
        {
          db: state.db,
          collection: "appointments",
          parameters: [{pid: patient.pid}],
          method: "find",
        },
      ],
      null,
      abortControllerRef.current.signal
    )
      .then(async (fsub) => {
        let res = await crud(
          state,
          [
            {
              db: state.db,
              collection: "forms",
              parameters: [{}],
              method: "find",
            },
          ],
          null,
          abortControllerRef.current.signal
        );

        setForms(res.data[0]);
        let sbforms = fsub.data?.[0] || [];
        let appts = fsub.data?.[1] || [];
        const map = {};
        appts.forEach((a) => {
          if (a?.aid) map[a.aid] = a;
        });
        setAppointmentMap(map);
        let arr = sbforms.reduce(
          (ac, e) => {
            if (e.completed) {
              ac[1].push(e);
            } else ac[0].push(e);
            return ac;
          },
          [[], []]
        );
        setAssignedForms(arr[0]);
        setCompletedForms(arr[1]);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
      });
  }, [state]);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    updatedFormValues();

    return () => {
      abortControllerRef.current.abort();
    };
  }, [state]);

  // The server says this patient's forms changed — a clinic assigned one, or
  // they completed one on another device, or a PDF finished rendering. The
  // event carries no answers on purpose, so this refetches rather than trusting
  // the wire. It is only a HINT: a socket on a backgrounded phone or a switched
  // network misses events, so nothing here is the only path to fresh data — the
  // effect above still runs on mount.
  useEffect(() => {
    const onChanged = () => updatedFormValues();
    window.addEventListener("brightlight:form-submission-changed", onChanged);
    return () => {
      window.removeEventListener("brightlight:form-submission-changed", onChanged);
    };
  }, [updatedFormValues]);

  useEffect(() => {
    let cancelled = false;
    if (!patient?.pid) return undefined;
    // Failure here is deliberately silent: the consent banner is an extra
    // prompt, and a consent lookup problem must not stop the patient from
    // seeing and completing their forms.
    fetchConsentStatus(state, patient.pid)
      .then((data) => {
        if (!cancelled) setConsentStatus(data?.status || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [state, patient?.pid]);

  const patientLocationIds = useMemo(
    () => normalizeLocationIds(patient?.lid),
    [patient?.lid]
  );
  const visibleAssignedForms = useMemo(
    () => filterFormsByLocation(assignedForms, patientLocationIds),
    [assignedForms, patientLocationIds]
  );
  const visibleCompletedForms = useMemo(
    () => filterFormsByLocation(completedForms, patientLocationIds),
    [completedForms, patientLocationIds]
  );
  const filteredForms =
    activeTab === "assigned"
      ? visibleAssignedForms
      : activeTab === "completed"
      ? visibleCompletedForms
      : [...visibleAssignedForms, ...visibleCompletedForms];

  const assignedCount = visibleAssignedForms.length;
  const completedCount = visibleCompletedForms.length;
  const totalCount = assignedCount + completedCount;

  return (
    <>
      {viewForm && (
        <ViewCompletedFrom form={viewForm} close={() => setViewForm(null)} />
      )}
      {completeForm && (
        <CompleteForm
          form={completeForm}
          close={(result) => {
            if (result?.completed && result?.fsid) {
              setAssignedForms((prev) => prev.filter((entry) => entry.fsid !== result.fsid));
              setCompletedForms((prev) => [{...completeForm, ...result.form, completed: true}, ...prev]);
            } else if (result?.savedAsDraft && result?.fsid) {
              setAssignedForms((prev) =>
                prev.map((entry) =>
                  entry.fsid === result.fsid
                    ? {...entry, ...result.form, completed: false, status: "draft"}
                    : entry
                )
              );
            }
            setCompleteForm(null);
          }}
          state={state}
        />
      )}

      <Box flex={1} display="flex" flexDirection="column">
        {/* Only an EXPLICIT request or a consent that went out of date raises
        this. A patient who was never asked is not nagged: the clinic starts
        the flow, and clinics that don't submit electronically never do. */}
        {(consentStatus === "requested" || consentStatus === "stale") && (
            <Flex
              align="center"
              justify="space-between"
              gap={3}
              mb={4}
              px={4}
              py={3}
              bg="orange.50"
              border="1px solid"
              borderColor="orange.200"
              borderRadius="8px"
              wrap="wrap"
            >
              <Box>
                <Text fontSize="13px" fontWeight="600" color="#7c4a03">
                  {consentStatus === "stale"
                    ? "Your insurance consent needs to be signed again"
                    : "A consent form needs your signature"}
                </Text>
                <Text fontSize="12px" color="#8a6d3b" mt={0.5}>
                  {consentStatus === "stale"
                    ? "Your insurance details changed since you last signed."
                    : "Your clinic needs this before submitting insurance claims for you."}
                </Text>
              </Box>
              <Button
                size="sm"
                colorScheme="orange"
                onClick={() => navigate("/consents", {state})}
              >
                Review and sign
              </Button>
            </Flex>
          )}

        {/* Filter tabs */}
        <Flex
          gap={1}
          mb={4}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
          p="3px"
          w="fit-content"
        >
          <FilterTab
            label="All"
            count={totalCount}
            isActive={activeTab === "all"}
            onClick={() => setActiveTab("all")}
          />
          <FilterTab
            label="Assigned"
            count={assignedCount}
            isActive={activeTab === "assigned"}
            onClick={() => setActiveTab("assigned")}
            accentColor="#6772e5"
          />
          <FilterTab
            label="Completed"
            count={completedCount}
            isActive={activeTab === "completed"}
            onClick={() => setActiveTab("completed")}
            accentColor="#34a853"
          />
        </Flex>

        {/* Forms list */}
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
          flex={1}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          {/* Table header */}
          <Flex
            px={4}
            py={2.5}
            borderBottom="1px solid"
            borderColor="gray.100"
            bg="gray.50"
            borderTopRadius="8px"
            flexShrink={0}
          >
            <Text
              fontSize="11px"
              fontWeight="600"
              color="#6b7280"
              textTransform="uppercase"
              letterSpacing="0.05em"
              flex={1}
            >
              Form
            </Text>
            <Text
              fontSize="11px"
              fontWeight="600"
              color="#6b7280"
              textTransform="uppercase"
              letterSpacing="0.05em"
              w="100px"
              display={{base: "none", md: "block"}}
            >
              Assigned
            </Text>
            <Text
              fontSize="11px"
              fontWeight="600"
              color="#6b7280"
              textTransform="uppercase"
              letterSpacing="0.05em"
              w="100px"
              display={{base: "none", md: "block"}}
            >
              Access
            </Text>
            <Text
              fontSize="11px"
              fontWeight="600"
              color="#6b7280"
              textTransform="uppercase"
              letterSpacing="0.05em"
              w="100px"
            >
              Status
            </Text>
            <Box w="80px" />
          </Flex>

          {/* Scrollable rows */}
          <Box flex={1} overflowY="auto">
            {loading ? (
              <Flex justify="center" align="center" py={12}>
                <Spinner size="sm" color="#6772e5" mr={3} />
                <Text fontSize="13px" color="#6b7280">
                  Loading forms...
                </Text>
              </Flex>
            ) : filteredForms.length === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={12}
              >
                <Icon
                  as={FiFileText}
                  fontSize="32px"
                  color="gray.300"
                  mb={3}
                />
                <Text fontSize="14px" color="#6b7280" fontWeight="500">
                  {activeTab === "assigned"
                    ? "No assigned forms"
                    : activeTab === "completed"
                    ? "No completed forms"
                    : "No forms yet"}
                </Text>
                <Text fontSize="12px" color="gray.400" mt={1}>
                  {activeTab === "assigned"
                    ? "You're all caught up"
                    : "Forms assigned by your provider will appear here"}
                </Text>
              </Flex>
            ) : (
              <GroupedFormsList
                forms={filteredForms}
                appointmentMap={appointmentMap}
                completedForms={completedForms}
                onView={setViewForm}
                onComplete={setCompleteForm}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

function FilterTab({label, count, isActive, onClick, accentColor}) {
  return (
    <Button
      size="xs"
      variant="ghost"
      onClick={onClick}
      bg={isActive ? "#f8f9fa" : "transparent"}
      color={isActive ? "#1a1f36" : "#6b7280"}
      fontWeight={isActive ? "600" : "500"}
      fontSize="12px"
      borderRadius="6px"
      px={3}
      py={1}
      h="28px"
      _hover={{bg: "#f8f9fa"}}
      transition="all 0.15s ease"
    >
      {label}
      {count > 0 && (
        <Badge
          ml={1.5}
          fontSize="10px"
          fontWeight="600"
          bg={
            isActive && accentColor
              ? accentColor
              : isActive
              ? "#1a1f36"
              : "gray.200"
          }
          color={isActive ? "white" : "#6b7280"}
          borderRadius="full"
          px={1.5}
          minW="18px"
          textAlign="center"
          lineHeight="18px"
        >
          {count}
        </Badge>
      )}
    </Button>
  );
}

function FormRow({data, completed, isLast, onClick}) {
  const isDraft = !completed && data?.status === "draft";
  const assigned = (() => {
    try {
      return lightFormat(new Date(data?.assignedDate), "MMM d, yyyy");
    } catch {
      return "—";
    }
  })();

  const access = completed ? "Completed" : "Available";

  return (
    <Flex
      as="button"
      onClick={() => onClick?.(data)}
      w="100%"
      textAlign="left"
      px={4}
      py={3}
      align="center"
      borderBottom={isLast ? "none" : "1px solid"}
      borderColor="gray.100"
      cursor="pointer"
      transition="all 0.12s ease"
      _hover={{bg: "#fafbfc"}}
      role="group"
    >
      {/* Form icon + name */}
      <Flex flex={1} align="center" gap={3} mr={3} minW={0}>
        <Flex
          w="32px"
          h="32px"
          borderRadius="6px"
          bg={completed ? "green.50" : isDraft ? "blue.50" : "#f0f0ff"}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon
            as={completed ? FiCheckCircle : FiFileText}
            fontSize="14px"
            color={completed ? "#34a853" : "#6772e5"}
          />
        </Flex>
        <Text
          fontSize="13px"
          fontWeight="500"
          color="#1a1f36"
          noOfLines={1}
        >
          {data.formTitle}
        </Text>
      </Flex>

      {/* Assigned date */}
      <Text
        fontSize="12px"
        color="#6b7280"
        w="100px"
        flexShrink={0}
        display={{base: "none", md: "block"}}
      >
        {assigned}
      </Text>

      {/* Access state */}
      <Text
        fontSize="12px"
        color="#6b7280"
        w="100px"
        flexShrink={0}
        display={{base: "none", md: "block"}}
      >
        {access}
      </Text>

      {/* Status */}
      <Box w="100px" flexShrink={0}>
        <Flex
          display="inline-flex"
          align="center"
          gap={1.5}
          px={2}
          py={0.5}
          borderRadius="full"
          bg={completed ? "green.50" : isDraft ? "blue.50" : "orange.50"}
          w="fit-content"
        >
          <Box
            w="5px"
            h="5px"
            borderRadius="full"
            bg={completed ? "#34a853" : isDraft ? "#6772e5" : "#e8a23e"}
          />
          <Text
            fontSize="11px"
            fontWeight="500"
            color={completed ? "#34a853" : isDraft ? "#6772e5" : "#c17a15"}
          >
            {completed ? "Done" : isDraft ? "Draft" : "Pending"}
          </Text>
        </Flex>
      </Box>

      {/* Action */}
      <Flex w="80px" justify="flex-end">
        <Button
          size="xs"
          variant="ghost"
          color="#6b7280"
          fontSize="12px"
          fontWeight="500"
          leftIcon={
            <Icon
              as={completed ? FiEye : FiEdit3}
              fontSize="12px"
            />
          }
          _hover={{color: "#6772e5", bg: "#f0f0ff"}}
          _groupHover={{color: "#6772e5"}}
          transition="all 0.15s ease"
          borderRadius="6px"
          px={2}
        >
          {completed ? "View" : isDraft ? "Continue" : "Fill"}
        </Button>
      </Flex>
    </Flex>
  );
}

function FormGroupRows({forms, completedForms, onView, onComplete}) {
  return forms.map((form, i) => {
    const isCompleted = completedForms.some((f) => f.fsid === form.fsid);
    return (
      <FormRow
        key={form.fsid || i}
        data={form}
        completed={isCompleted}
        isLast={i === forms.length - 1}
        onClick={() => (isCompleted ? onView(form) : onComplete(form))}
      />
    );
  });
}

function AppointmentGroup({appt, forms, completedForms, onView, onComplete}) {
  const [collapsed, setCollapsed] = useState(false);
  const service = appt?.service || appt?.serviceName || "Appointment";
  const provider = getApptProvider(appt);
  const dateStr = formatApptDate(appt);

  return (
    <Box borderBottom="1px solid" borderColor="gray.100">
      <Flex
        align="center"
        cursor="pointer"
        onClick={() => setCollapsed((c) => !c)}
        px={4}
        py={2.5}
        bg="gray.50"
        _hover={{bg: "gray.100"}}
        transition="all 0.15s ease"
        userSelect="none"
      >
        <Box color="#6b7280" mr={1} flexShrink={0}>
          {collapsed ? <MdChevronRight size={18} /> : <MdExpandMore size={18} />}
        </Box>
        <Box flex={1} minW={0}>
          <Text fontSize="13px" fontWeight="600" color="#1a1f36" noOfLines={1}>
            {service}
          </Text>
          <Flex gap={1} mt={0} align="center">
            {dateStr && (
              <Text fontSize="11px" color="#6b7280">
                {dateStr}
              </Text>
            )}
            {dateStr && provider && (
              <Text fontSize="11px" color="gray.300">
                ·
              </Text>
            )}
            {provider && (
              <Text fontSize="11px" color="#6b7280" isTruncated>
                {provider}
              </Text>
            )}
          </Flex>
        </Box>
        <Text fontSize="11px" color="gray.400" flexShrink={0} ml={2}>
          {forms.length} form{forms.length !== 1 ? "s" : ""}
        </Text>
      </Flex>
      {!collapsed && (
        <Box>
          <FormGroupRows
            forms={forms}
            completedForms={completedForms}
            onView={onView}
            onComplete={onComplete}
          />
        </Box>
      )}
    </Box>
  );
}

function UngroupedSection({forms, completedForms, onView, onComplete}) {
  const [collapsed, setCollapsed] = useState(false);
  if (forms.length === 0) return null;

  return (
    <Box borderBottom="1px solid" borderColor="gray.100">
      <Flex
        align="center"
        cursor="pointer"
        onClick={() => setCollapsed((c) => !c)}
        px={4}
        py={2.5}
        bg="gray.50"
        _hover={{bg: "gray.100"}}
        transition="all 0.15s ease"
        userSelect="none"
      >
        <Box color="#6b7280" mr={1} flexShrink={0}>
          {collapsed ? <MdChevronRight size={18} /> : <MdExpandMore size={18} />}
        </Box>
        <Box flex={1} minW={0}>
          <Text fontSize="13px" fontWeight="600" color="#1a1f36">
            Other forms
          </Text>
          <Text fontSize="11px" color="#6b7280">
            Assigned directly
          </Text>
        </Box>
        <Text fontSize="11px" color="gray.400" flexShrink={0} ml={2}>
          {forms.length} form{forms.length !== 1 ? "s" : ""}
        </Text>
      </Flex>
      {!collapsed && (
        <Box>
          <FormGroupRows
            forms={forms}
            completedForms={completedForms}
            onView={onView}
            onComplete={onComplete}
          />
        </Box>
      )}
    </Box>
  );
}

function GroupedFormsList({forms, appointmentMap, completedForms, onView, onComplete}) {
  const {sortedGroups, ungrouped} = useMemo(
    () => groupFormsByAppointment(forms, appointmentMap),
    [forms, appointmentMap]
  );

  return (
    <Box>
      {sortedGroups.map(([aid, groupForms]) => (
        <AppointmentGroup
          key={aid}
          appt={appointmentMap[aid]}
          forms={groupForms}
          completedForms={completedForms}
          onView={onView}
          onComplete={onComplete}
        />
      ))}
      <UngroupedSection
        forms={ungrouped}
        completedForms={completedForms}
        onView={onView}
        onComplete={onComplete}
      />
    </Box>
  );
}
